import type * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@shared/lib/utils';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const headingVariants = cva('text-balance text-foreground', {
  variants: {
    size: {
      xs: 'text-xs/normal font-semibold',
      sm: 'text-sm/4.5 font-semibold',
      base: 'text-base/5 font-medium',
      lg: `
        text-base/5 font-medium tracking-[calc(var(--tracking-tight)/2)]
        md:text-lg/6
      `,
      xl: `
        text-lg font-medium tracking-[calc(var(--tracking-tight)/2)]
        md:text-xl/6.5
      `,
      '2xl': `
        text-xl/6 font-medium tracking-[calc(var(--tracking-tight)/2)]
        lg:text-2xl/7.5
      `,
      '3xl': `
        text-xl font-medium tracking-tight
        md:text-2xl/7
        lg:text-3xl/8.5
      `,
      '4xl': `
        text-2xl/7 font-medium tracking-tight
        md:text-3xl/8.5
        lg:text-4xl/10
      `,
      '5xl': `
        text-3xl/8.5 font-medium tracking-tight
        md:text-4xl/10
        lg:text-5xl/none
      `,
      '6xl': `
        text-3xl/8.5 font-medium tracking-tight
        sm:text-4xl/10
        lg:text-5xl/none lg:tracking-tighter
        xl:text-6xl/none
      `,
      '7xl': `
        text-4xl/none font-medium tracking-tight
        sm:text-5xl/none
        md:text-6xl/none md:tracking-tighter
        lg:text-7xl/none lg:font-[460]
      `,
      '8xl': `
        text-5xl/none font-medium tracking-tight
        sm:text-6xl/none
        md:text-7xl/none md:tracking-tighter
        lg:text-8xl/23 lg:font-[460]
      `,
      '9xl': `
        text-5xl/none font-medium tracking-tight
        sm:text-6xl/none sm:font-[480] sm:tracking-tighter
        md:text-7xl/none md:font-[460]
        lg:text-8xl/23 lg:font-normal lg:tracking-tighter
        xl:text-9xl/30
      `,
      max: `
        text-[clamp(2.25rem,9vw,8rem)] leading-[clamp(0.9em,var(--dlh),1.15em)] tracking-tight
        [--dlh:calc((1.2-((1em/1rem)*0.04))*1em)]
        sm:tracking-tighter
      `,
    },
    weight: {
      medium: 'font-medium!',
      normal: 'font-normal!',
    },
    aboveCard: {
      true: 'ms-1',
      false: null,
    },
  },
  defaultVariants: {
    size: 'base',
    aboveCard: false,
  },
});

type HeadingSize = NonNullable<VariantProps<typeof headingVariants>['size']>;

type HeadingProps = React.HTMLAttributes<HTMLHeadingElement> &
  VariantProps<typeof headingVariants> & {
    tag?: HeadingTag;
  };

const Heading = ({
  tag: Tag = 'h2',
  size = 'base',
  aboveCard = false,
  weight,
  className,
  ...props
}: HeadingProps) => {
  return (
    <Tag
      data-slot="heading"
      className={cn(headingVariants({ size, aboveCard, weight: weight ?? undefined }), className)}
      {...props}
    />
  );
};

export { Heading };
export type { HeadingProps, HeadingSize, HeadingTag };
